// CR075: S1_EXTRACT Flyer Diagnostics Panel - v3 with timing + live progress
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, CheckCircle2, XCircle, ChevronDown, Copy, FileText, Image, Brain, Database, Download, Lightbulb, Clock, AlertTriangle, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface DiagnosticsStage {
  ok: boolean;
  count?: number;
  errors: Array<{ id?: string; code: string; message: string }>;
}

interface TimingMs {
  total: number;
  download_signed_urls: number;
  render_check: number;
  ocr_total: number;
  ocr_per_page: number[];
  candidate_scoring_total: number;
  ai_total: number;
  ai_per_call: number[];
  write_staging_total: number;
}

interface CandidateTop {
  page_number: number;
  score: number;
  reasons: string[];
}

interface AiError {
  candidate_index: number;
  page_number: number;
  code: string;
  message: string;
  duration_ms: number;
}

interface LiveProgress {
  current_flyer_key: string | null;
  current_page_number: number | null;
  pages_total: number;
  pages_processed: number;
  candidates_total: number;
  candidate_index: number;
  ai_calls_started: number;
  ai_calls_completed: number;
  ai_call_started_at: string | null;
  ai_call_duration_ms: number | null;
  write_rows_attempted: number;
  write_rows_succeeded: number;
}

interface FlyerExtractDiagnostics {
  schema_version: string;
  batch_run_id: string;
  source_attempt_id: string | null;
  started_at: string;
  ended_at: string | null;
  status: string;
  stage: string;
  last_heartbeat_at?: string;
  last_progress_at: string;
  live_progress?: LiveProgress;
  
  inputs: {
    placeholders_found: number;
    placeholder_ids: string[];
    storage_bucket: string;
    file_types: string[];
    rendered_count: number;
    not_rendered_count: number;
  };
  
  stages: {
    download: DiagnosticsStage & { signed_url_ok?: boolean };
    render: DiagnosticsStage & { pdfs_rendered: number; pages_rendered_total: number };
    ocr: DiagnosticsStage & { pages_processed: number; avg_ocr_score: number | null; total_pages_in_pdf?: number };
    candidate_scoring: DiagnosticsStage & { candidates_count: number; candidate_page_indices?: number[]; threshold: number; top_signals: string[] };
    deep_scan: DiagnosticsStage & { ai_model: string; ai_calls: number; ai_calls_succeeded?: number; ai_calls_failed?: number; pages_deep_scanned?: number; pages_deep_scanned_indices?: number[] };
    write_staging: DiagnosticsStage & { rows_attempted: number; rows_inserted: number };
  };
  
  counts: {
    records_in: number;
    total_pages_in_pdf?: number;
    pages_total?: number;
    pages_ocrd?: number;
    candidates_count?: number;
    candidates_total?: number;
    pages_deep_scanned?: number;
    deals_extracted: number;
    pages_skipped_limit?: number;
    ai_calls_skipped_limit?: number;
  };
  
  ai_errors?: AiError[];
  timing_ms?: TimingMs;
  
  limits?: {
    max_pages_per_flyer: number;
    max_total_pages: number;
    max_ai_calls: number;
    ai_call_timeout_ms: number;
    ocr_page_timeout_ms: number;
  };
  
  candidates_top?: CandidateTop[];
  limit_reached?: string;
  zero_deals_reason?: string;
  
  config_snapshot: {
    candidateThreshold: number;
    minOcrScore: number;
    maxAiCalls: number;
    model: string;
    signalsCount?: number;
  };
  
  recommended_action?: string;
  
  // Per-page diagnostics for Diagnostics v2 parity
  page_diagnostics?: Array<{
    page_number: number;
    storage_path: string;
    ocr_text_preview: string; // Capped at 500 chars
    ocr_text_chars_total: number;
    ocr_score: number;
    ocr_duration_ms: number;
    is_candidate: boolean;
    candidate_score: number;
    candidate_reasons: string[];
    ai_scanned: boolean;
    ai_result?: { deals_found: number; duration_ms: number; error?: string };
  }>;
  
  // Explicit deep scan summary for verification
  deep_scan_summary?: {
    deep_scan_calls: number;
    pages_deep_scanned: number;
    candidate_page_indices: number[];
  };
}

interface S1ExtractDiagnosticsPanelProps {
  diagnostics: FlyerExtractDiagnostics;
}

function StageStatusIcon({ ok }: { ok: boolean }) {
  if (ok) {
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }
  return <XCircle className="h-4 w-4 text-destructive" />;
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function StageRow({ 
  label, 
  icon, 
  ok, 
  stats, 
  timing,
  errors 
}: { 
  label: string; 
  icon: React.ReactNode;
  ok: boolean; 
  stats: string;
  timing?: string;
  errors: Array<{ id?: string; code: string; message: string }>;
}) {
  const [isOpen, setIsOpen] = useState(!ok && errors.length > 0);
  
  return (
    <div className={`border rounded-lg ${ok ? "border-border" : "border-destructive/50 bg-destructive/5"}`}>
      <div 
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <StageStatusIcon ok={ok} />
          <div className="flex items-center gap-2 text-sm font-medium">
            {icon}
            {label}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{stats}</span>
          {timing && (
            <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
              {timing}
            </span>
          )}
          {errors.length > 0 && (
            <Badge variant="destructive" className="text-xs">
              {errors.length} error{errors.length > 1 ? "s" : ""}
            </Badge>
          )}
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>
      {isOpen && errors.length > 0 && (
        <div className="px-3 pb-3 border-t border-border/50">
          <ul className="text-xs space-y-1 mt-2 max-h-32 overflow-auto">
            {errors.slice(0, 10).map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-destructive">
                <span className="font-mono bg-destructive/10 px-1 rounded">{e.code}</span>
                <span className="text-muted-foreground">{e.message}</span>
              </li>
            ))}
            {errors.length > 10 && (
              <li className="text-muted-foreground italic">...and {errors.length - 10} more</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export function S1ExtractDiagnosticsPanel({ diagnostics }: S1ExtractDiagnosticsPanelProps) {
  const { toast } = useToast();
  const [showRawJson, setShowRawJson] = useState(false);
  const [showTimingDetails, setShowTimingDetails] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [showPageDetails, setShowPageDetails] = useState(false);

  const { stages, inputs, counts, config_snapshot, recommended_action, status, timing_ms, limits, candidates_top, limit_reached, stage, last_progress_at, page_diagnostics } = diagnostics;

  // Calculate progress for running state
  const totalPages = counts.total_pages_in_pdf || counts.pages_total || stages.render.pages_rendered_total || 0;
  const pagesProcessed = counts.pages_ocrd || stages.ocr.pages_processed || 0;
  const progressPercent = totalPages > 0 ? Math.round((pagesProcessed / totalPages) * 100) : 0;
  
  // Check if stuck (last progress > 90s ago and still running)
  const isRunning = status === "RUNNING";
  const lastProgressDate = last_progress_at ? new Date(last_progress_at) : null;
  const secondsSinceProgress = lastProgressDate ? (Date.now() - lastProgressDate.getTime()) / 1000 : 0;
  const isLikelyStuck = isRunning && secondsSinceProgress > 90;

  const copyDiagnostics = () => {
    navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
    toast({ title: "Copied", description: "Diagnostics JSON copied to clipboard" });
  };

  // Get avg timing per operation
  const avgOcrMs = timing_ms?.ocr_per_page?.length 
    ? Math.round(timing_ms.ocr_per_page.reduce((a, b) => a + b, 0) / timing_ms.ocr_per_page.length)
    : null;
  const avgAiMs = timing_ms?.ai_per_call?.length 
    ? Math.round(timing_ms.ai_per_call.reduce((a, b) => a + b, 0) / timing_ms.ai_per_call.length)
    : null;

  // Identify bottleneck
  let bottleneck: string | null = null;
  if (timing_ms) {
    const stages = [
      { name: "OCR", time: timing_ms.ocr_total },
      { name: "AI Deep Scan", time: timing_ms.ai_total },
      { name: "Signed URLs", time: timing_ms.download_signed_urls },
      { name: "Write Staging", time: timing_ms.write_staging_total },
    ];
    const sorted = [...stages].sort((a, b) => b.time - a.time);
    if (sorted[0].time > 0 && sorted[0].time > timing_ms.total * 0.4) {
      bottleneck = sorted[0].name;
    }
  }

  return (
    <div className="space-y-4 p-3 bg-muted/50 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          S1_EXTRACT Flyer Diagnostics (v3)
        </div>
        <div className="flex items-center gap-2">
          {timing_ms?.total && (
            <span className="text-xs text-muted-foreground font-mono">
              {formatMs(timing_ms.total)}
            </span>
          )}
          <Badge 
            variant={
              status === "COMPLETE" ? "default" : 
              status === "LIMIT_REACHED" ? "secondary" :
              status === "PARTIAL" ? "secondary" : 
              status === "RUNNING" ? "outline" :
              "destructive"
            }
            className="text-xs"
          >
            {status}
          </Badge>
        </div>
      </div>

      {/* Live Progress Banner (when running) */}
      {isRunning && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
              <Zap className="h-4 w-4 animate-pulse" />
              Processing: {stage}
            </div>
            <span className="text-xs text-blue-600">
              {pagesProcessed}/{totalPages} pages · {stages.deep_scan.ai_calls} AI calls
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          {lastProgressDate && (
            <div className="text-xs text-blue-600 mt-1">
              Last update: {formatDistanceToNow(lastProgressDate, { addSuffix: true })}
            </div>
          )}
        </div>
      )}

      {/* Stuck Warning */}
      {isLikelyStuck && (
        <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-orange-700 dark:text-orange-300">Likely Stuck</div>
            <div className="text-sm text-orange-600 dark:text-orange-400">
              No progress for {Math.round(secondsSinceProgress)}s at stage: <strong>{stage}</strong>. 
              Check edge function logs for errors.
            </div>
          </div>
        </div>
      )}

      {/* Limit Reached Banner */}
      {limit_reached && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-amber-700 dark:text-amber-300">Limit Reached</div>
            <div className="text-sm text-amber-600 dark:text-amber-400">{limit_reached}</div>
            {counts.pages_skipped_limit && counts.pages_skipped_limit > 0 && (
              <div className="text-xs text-amber-500 mt-1">
                {counts.pages_skipped_limit} pages skipped due to limits
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottleneck Indicator */}
      {bottleneck && !isRunning && (
        <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-start gap-3">
          <Clock className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-purple-700 dark:text-purple-300">Bottleneck Identified</div>
            <div className="text-sm text-purple-600 dark:text-purple-400">
              <strong>{bottleneck}</strong> consumed most of the processing time.
              {bottleneck === "OCR" && avgOcrMs && ` Avg ${formatMs(avgOcrMs)}/page.`}
              {bottleneck === "AI Deep Scan" && avgAiMs && ` Avg ${formatMs(avgAiMs)}/call.`}
            </div>
          </div>
        </div>
      )}

      {/* Recommended Action Banner */}
      {recommended_action && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
          <Lightbulb className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Recommended Next Action</div>
            <div className="text-sm text-yellow-600 dark:text-yellow-400">{recommended_action}</div>
          </div>
        </div>
      )}

      {/* Truth Table - Pipeline Funnel */}
      <div className="p-3 bg-background rounded-lg border">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Pipeline Truth Table
        </div>
        <div className="grid grid-cols-7 gap-2 text-center text-xs">
          <div className="flex flex-col items-center p-2 bg-muted/50 rounded">
            <FileText className="h-4 w-4 mb-1 text-blue-500" />
            <span className="font-bold text-lg">{inputs.placeholders_found}</span>
            <span className="text-muted-foreground">Flyers Queued</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-muted/50 rounded">
            <Image className="h-4 w-4 mb-1 text-purple-500" />
            <span className={`font-bold text-lg ${inputs.rendered_count === 0 ? "text-destructive" : "text-green-600"}`}>
              {inputs.rendered_count}
            </span>
            <span className="text-muted-foreground">Rendered</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-muted/50 rounded">
            <Image className="h-4 w-4 mb-1 text-indigo-500" />
            <span className="font-bold text-lg">{totalPages}</span>
            <span className="text-muted-foreground">Total Pages</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-muted/50 rounded">
            <FileText className="h-4 w-4 mb-1 text-orange-500" />
            <span className="font-bold text-lg">{pagesProcessed}</span>
            <span className="text-muted-foreground">Pages OCR'd</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-muted/50 rounded">
            <AlertCircle className="h-4 w-4 mb-1 text-amber-500" />
            <span className="font-bold text-lg">{counts.candidates_count || stages.candidate_scoring.candidates_count}</span>
            <span className="text-muted-foreground">Candidates</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-muted/50 rounded">
            <Brain className="h-4 w-4 mb-1 text-purple-600" />
            <span className="font-bold text-lg">{stages.deep_scan.ai_calls}</span>
            <span className="text-muted-foreground">AI Calls</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-muted/50 rounded">
            <Database className="h-4 w-4 mb-1 text-green-600" />
            <span className={`font-bold text-lg ${counts.deals_extracted === 0 ? "text-destructive" : "text-green-600"}`}>
              {counts.deals_extracted}
            </span>
            <span className="text-muted-foreground">Deals</span>
          </div>
        </div>
      </div>

      {/* Inputs Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <span className="text-muted-foreground">Placeholders:</span>{" "}
          <span className="font-medium">{inputs.placeholders_found}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Rendered:</span>{" "}
          <span className={`font-medium ${inputs.rendered_count === 0 ? "text-destructive" : "text-green-600"}`}>
            {inputs.rendered_count}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Not Rendered:</span>{" "}
          <span className={`font-medium ${inputs.not_rendered_count > 0 ? "text-yellow-600" : ""}`}>
            {inputs.not_rendered_count}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">File Types:</span>{" "}
          <span className="font-mono text-xs">{[...new Set(inputs.file_types)].join(", ") || "—"}</span>
        </div>
      </div>

      {/* Stages */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Processing Stages
        </div>
        
        <StageRow
          label="Download / Signed URL"
          icon={<Download className="h-4 w-4" />}
          ok={stages.download.ok}
          stats={stages.download.signed_url_ok ? "URLs OK" : "URL errors"}
          timing={timing_ms?.download_signed_urls ? formatMs(timing_ms.download_signed_urls) : undefined}
          errors={stages.download.errors}
        />
        
        <StageRow
          label="Render (PDF → Images)"
          icon={<Image className="h-4 w-4" />}
          ok={stages.render.ok}
          stats={`${stages.render.pdfs_rendered} PDFs → ${stages.render.pages_rendered_total} pages`}
          timing={timing_ms?.render_check ? formatMs(timing_ms.render_check) : undefined}
          errors={stages.render.errors}
        />
        
        <StageRow
          label="OCR"
          icon={<FileText className="h-4 w-4" />}
          ok={stages.ocr.ok}
          stats={`${stages.ocr.pages_processed} pages${stages.ocr.avg_ocr_score ? ` (avg score: ${stages.ocr.avg_ocr_score})` : ""}`}
          timing={timing_ms?.ocr_total ? `${formatMs(timing_ms.ocr_total)}${avgOcrMs ? ` · ${formatMs(avgOcrMs)}/pg` : ""}` : undefined}
          errors={stages.ocr.errors}
        />
        
        <StageRow
          label="Candidate Scoring"
          icon={<AlertCircle className="h-4 w-4" />}
          ok={stages.candidate_scoring.ok}
          stats={`${stages.candidate_scoring.candidates_count} candidates (threshold: ${stages.candidate_scoring.threshold})`}
          timing={timing_ms?.candidate_scoring_total ? formatMs(timing_ms.candidate_scoring_total) : undefined}
          errors={stages.candidate_scoring.errors}
        />
        
        <StageRow
          label="AI Deep Scan"
          icon={<Brain className="h-4 w-4" />}
          ok={stages.deep_scan.ok}
          stats={`${stages.deep_scan.ai_calls} calls on ${stages.deep_scan.pages_deep_scanned || stages.deep_scan.ai_calls} pages · ${stages.deep_scan.ai_model.split("/").pop()}`}
          timing={timing_ms?.ai_total ? `${formatMs(timing_ms.ai_total)}${avgAiMs ? ` · ${formatMs(avgAiMs)}/call` : ""}` : undefined}
          errors={stages.deep_scan.errors}
        />
        
        <StageRow
          label="Write to Staging"
          icon={<Database className="h-4 w-4" />}
          ok={stages.write_staging.ok}
          stats={`${stages.write_staging.rows_inserted}/${stages.write_staging.rows_attempted} rows`}
          timing={timing_ms?.write_staging_total ? formatMs(timing_ms.write_staging_total) : undefined}
          errors={stages.write_staging.errors}
        />
      </div>

      {/* Counts Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm p-3 bg-background rounded-lg border">
        <div>
          <span className="text-muted-foreground">Records In:</span>{" "}
          <span className="font-medium">{counts.records_in}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Pages OCR'd:</span>{" "}
          <span className="font-medium">{counts.pages_ocrd || stages.ocr.pages_processed}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Candidates:</span>{" "}
          <span className="font-medium">{counts.candidates_count || stages.candidate_scoring.candidates_count}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Deals Extracted:</span>{" "}
          <span className={`font-medium ${counts.deals_extracted === 0 ? "text-destructive" : "text-green-600"}`}>
            {counts.deals_extracted}
          </span>
        </div>
      </div>

      {/* Per-Page OCR Details (Diagnostics v2 parity) */}
      {page_diagnostics && page_diagnostics.length > 0 && (
        <Collapsible open={showPageDetails} onOpenChange={setShowPageDetails}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-2">
                <FileText className="h-3 w-3" />
                Per-Page OCR Details ({page_diagnostics.length} pages)
              </span>
              <ChevronDown className={`h-3 w-3 transition-transform ${showPageDetails ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-2 mt-2 max-h-96 overflow-auto">
              {page_diagnostics.map((page) => (
                <div key={page.page_number} className={`p-3 rounded-lg border text-xs ${page.is_candidate ? "border-green-500/50 bg-green-500/5" : "border-border"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">Page {page.page_number}</span>
                      <Badge variant="outline" className="text-[10px]">OCR: {page.ocr_score}</Badge>
                      <Badge variant={page.is_candidate ? "default" : "secondary"} className="text-[10px]">
                        {page.is_candidate ? `Candidate (${page.candidate_score})` : "Not candidate"}
                      </Badge>
                      {page.ai_scanned && <Badge className="bg-purple-600 text-[10px]">AI Scanned</Badge>}
                    </div>
                    <span className="text-muted-foreground">{formatMs(page.ocr_duration_ms)}</span>
                  </div>
                  {page.candidate_reasons.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {page.candidate_reasons.map((r, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] bg-muted/50">{r}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="bg-muted/30 rounded p-2 max-h-24 overflow-auto">
                    <pre className="text-[10px] font-mono whitespace-pre-wrap">
                      {page.ocr_text_preview || "No text extracted"}
                      {page.ocr_text_chars_total > 500 && (
                        <span className="text-muted-foreground"> ... [{page.ocr_text_chars_total - 500} more chars]</span>
                      )}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Top Candidates (if available) */}
      {candidates_top && candidates_top.length > 0 && (
        <Collapsible open={showCandidates} onOpenChange={setShowCandidates}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground">
              Top Candidates ({candidates_top.length})
              <ChevronDown className={`h-3 w-3 transition-transform ${showCandidates ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-1 mt-1 text-xs">
              {candidates_top.map((c, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-background rounded border">
                  <span className="font-mono text-muted-foreground">pg {c.page_number}</span>
                  <Badge variant="outline" className="text-xs">score: {c.score}</Badge>
                  <span className="text-muted-foreground">{c.reasons.slice(0, 3).join(", ")}</span>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Timing Details */}
      {timing_ms && (
        <Collapsible open={showTimingDetails} onOpenChange={setShowTimingDetails}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                Timing Breakdown
              </span>
              <ChevronDown className={`h-3 w-3 transition-transform ${showTimingDetails ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid grid-cols-2 gap-2 text-xs p-2 bg-background rounded border mt-1">
              <div>
                <span className="text-muted-foreground">Total:</span>{" "}
                <span className="font-mono">{formatMs(timing_ms.total)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Signed URLs:</span>{" "}
                <span className="font-mono">{formatMs(timing_ms.download_signed_urls)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">OCR Total:</span>{" "}
                <span className="font-mono">{formatMs(timing_ms.ocr_total)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Candidate Scoring:</span>{" "}
                <span className="font-mono">{formatMs(timing_ms.candidate_scoring_total)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">AI Total:</span>{" "}
                <span className="font-mono">{formatMs(timing_ms.ai_total)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Write Staging:</span>{" "}
                <span className="font-mono">{formatMs(timing_ms.write_staging_total)}</span>
              </div>
              {timing_ms.ocr_per_page.length > 0 && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">OCR per page (first {timing_ms.ocr_per_page.length}):</span>{" "}
                  <span className="font-mono text-[10px]">
                    [{timing_ms.ocr_per_page.map(t => formatMs(t)).join(", ")}]
                  </span>
                </div>
              )}
              {timing_ms.ai_per_call.length > 0 && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">AI per call (first {timing_ms.ai_per_call.length}):</span>{" "}
                  <span className="font-mono text-[10px]">
                    [{timing_ms.ai_per_call.map(t => formatMs(t)).join(", ")}]
                  </span>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Limits & Config Snapshot */}
      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground">
            Config & Limits
            <ChevronDown className="h-3 w-3" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="grid grid-cols-2 gap-2 text-xs p-2 bg-background rounded border mt-1">
            <div>
              <span className="text-muted-foreground">Model:</span>{" "}
              <span className="font-mono">{config_snapshot.model}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Candidate Threshold:</span>{" "}
              <span className="font-mono">{config_snapshot.candidateThreshold}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Min OCR Score:</span>{" "}
              <span className="font-mono">{config_snapshot.minOcrScore}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Max AI Calls:</span>{" "}
              <span className="font-mono">{config_snapshot.maxAiCalls}</span>
            </div>
            {limits && (
              <>
                <div>
                  <span className="text-muted-foreground">Max Pages/Flyer:</span>{" "}
                  <span className="font-mono">{limits.max_pages_per_flyer}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Max Total Pages:</span>{" "}
                  <span className="font-mono">{limits.max_total_pages}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">OCR Timeout:</span>{" "}
                  <span className="font-mono">{formatMs(limits.ocr_page_timeout_ms)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">AI Timeout:</span>{" "}
                  <span className="font-mono">{formatMs(limits.ai_call_timeout_ms)}</span>
                </div>
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Raw JSON Toggle */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="text-xs gap-2" onClick={() => setShowRawJson(!showRawJson)}>
          {showRawJson ? "Hide" : "View"} Raw JSON
        </Button>
        <Button variant="ghost" size="sm" className="text-xs gap-2" onClick={copyDiagnostics}>
          <Copy className="h-3 w-3" />
          Copy
        </Button>
      </div>

      {showRawJson && (
        <pre className="text-xs bg-background p-3 rounded border overflow-auto max-h-64 font-mono">
          {JSON.stringify(diagnostics, null, 2)}
        </pre>
      )}
    </div>
  );
}
